'use client';

import { useSettingsContext } from '@/contexts/SettingsContext';
import { TListItem } from '@/types/types';
import { getAbsoluteLocalizedLink, getLocalizedLink } from '@/utils/getLocalizedLink';
import { getBreadcrumbSchema } from '@/utils/structuredData';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import classNames from 'classnames';
import Link from 'next/link';
import Script from 'next/script';
import React from 'react';

interface IBreadcrumbsProps {
    items?: TListItem[];
    id?: string;
}

const BreadcrumbItem: React.FC<{ item: TListItem; isLast: boolean }> = ({
    item: { key, value },
    isLast,
}) => {
    return (
        <li
            className={classNames(
                'tw-inline tw-font-gotham-book lg:tw-text-[12px] tw-text-[11px] tw-text-textBase',
                {
                    "after:tw-content-['>'] after:tw-px-[7px]": !isLast,
                },
            )}
        >
            {value && !isLast ? <Link href={value}>{key}</Link> : key}
        </li>
    );
};

const Breadcrumbs: React.FC<IBreadcrumbsProps> = ({ items, id }) => {
    const { lang } = useSettingsContext();
    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    const localizedItems = items?.map((item) => ({
        ...item,
        value: getLocalizedLink(item.value, lang),
    }));

    const breadcrumbSchema = items?.length
        ? getBreadcrumbSchema(
              items?.map((item) => ({
                  name: item.key,
                  url: getAbsoluteLocalizedLink(item.value, lang),
              })) || [],
          )
        : undefined;

    return (
        <>
            {breadcrumbSchema && (
                <Script
                    type="application/ld+json"
                    id="breadcrumb-schema"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(breadcrumbSchema),
                    }}
                />
            )}
            <section className="tw-block tw-pt-[19px] tw-pb-[9px] tw-mb-0">
                <div className="container">
                    <div
                        className="tw-w-full tw-p-0"
                        {...inspectorProps({
                            fieldId: 'breadcrumbs',
                        })}
                    >
                        <ul className="tw-m-o tw-p-0">
                            {localizedItems?.map((item, index) => (
                                <BreadcrumbItem
                                    item={item}
                                    key={item.id}
                                    isLast={index === localizedItems.length - 1}
                                />
                            ))}
                        </ul>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Breadcrumbs;
